import { createReducer } from '@reduxjs/toolkit';
import {
    setCollectionsRecords,
    setCollectionsLoading,
    removeCollectionRecord,
    setCollectionIdentifiers,
    addNewCollectionIdentifiers,
    setCollectionReaction,
    saveCollection,
    removeSavedCollectionRecord,
    addReportedCollection,
} from './actions';
import type { CollectionsDataModel } from './types';

const DEFAULT_NAMESPACE_STATE = {
    identifiers: [],
    isLoading: false,
};

interface CollectionsState {
    records: Record<string, CollectionsDataModel>;
    namespaces: Record<
        string,
        {
            identifiers: Array<CollectionsDataModel['id']>;
            isLoading: boolean;
        }
    >;
    identifiers: Array<CollectionsDataModel['id']>;
    isLoading: boolean;
    reportedCollections: Record<string, boolean>;
}

export const collectionsReducer = createReducer<CollectionsState>(
    {
        records: {},
        namespaces: {},
        identifiers: [],
        isLoading: false,
        reportedCollections: {},
    },
    (builder) => {
        builder
            .addCase(setCollectionsRecords, (draft, action) => {
                action.payload.forEach((collection) => {
                    draft.records[collection.id] = collection;
                });
            })
            .addCase(setCollectionIdentifiers, (draft, action) => {
                const { namespace, identifiers } = action.payload;

                draft.namespaces[namespace] = draft.namespaces[namespace] ?? {
                    ...DEFAULT_NAMESPACE_STATE,
                };
                draft.namespaces[namespace].identifiers = identifiers;
            })
            .addCase(saveCollection, (draft, action) => {
                Object.keys(draft.namespaces).forEach((namespace) => {
                    const record = draft.namespaces[namespace];
                    if (!record.identifiers.includes(action.payload)) {
                        record.identifiers = [...record.identifiers, action.payload];
                    }
                });

                if (!draft.records[action.payload]?.id) {
                    return;
                }

                draft.records[action.payload] = {
                    ...draft.records[action.payload],
                    isSaved: true,
                };
            })
            .addCase(addNewCollectionIdentifiers, (draft, action) => {
                const { namespace, identifiers } = action.payload;

                draft.namespaces[namespace] = draft.namespaces[namespace] ?? {
                    ...DEFAULT_NAMESPACE_STATE,
                };

                // первый элемент всегда дефолтная коллекция
                const currentIdentifiers = draft.namespaces[namespace].identifiers;
                draft.namespaces[namespace].identifiers = [
                    currentIdentifiers[0],
                    ...identifiers,
                    ...currentIdentifiers.slice(1),
                ];
            })
            .addCase(setCollectionsLoading, (draft, action) => {
                const { namespace, isLoading } = action.payload;

                draft.namespaces[namespace] = draft.namespaces[namespace] ?? {
                    ...DEFAULT_NAMESPACE_STATE,
                };
                draft.namespaces[namespace].isLoading = isLoading;
            })
            .addCase(setCollectionReaction, (draft, action) => {
                const { id, isLiked } = action.payload;

                if (draft.records[id]) {
                    draft.records[id].isLiked = isLiked;
                    draft.records[id].likes += isLiked ? 1 : -1;
                }
            })
            .addCase(removeCollectionRecord, (draft, action) => {
                delete draft.records[action.payload];

                Object.keys(draft.namespaces).forEach((namespace) => {
                    draft.namespaces[namespace].identifiers = draft.namespaces[
                        namespace
                    ].identifiers.filter((id: string) => id !== action.payload);
                });
            })
            .addCase(removeSavedCollectionRecord, (draft, action) => {
                draft.namespaces['default'].identifiers = draft.namespaces[
                    'default'
                ].identifiers.filter((id: string) => id !== action.payload);

                if (!draft.records[action.payload]?.id) {
                    return;
                }

                draft.records[action.payload] = {
                    ...draft.records[action.payload],
                    isSaved: false,
                };
            })

            .addCase(addReportedCollection, (draft, action) => {
                draft.reportedCollections[action.payload] = true;
            });
    },
);
